function detailsLevel(attr) {
  const d = attr?.Details ?? attr?.details
  if (d == null) return null
  try { return (typeof d === 'string' ? JSON.parse(d) : d)?.level } catch { return null }
}

/** Unify API shapes (Pascal from CMS vs camelCase JSON). */
function normalizeAttributeForTree(raw) {
  if (!raw || typeof raw !== 'object') return raw
  return {
    ...raw,
    AttributeID: raw.AttributeID ?? raw.attributeId,
    ParentAttributeID: raw.ParentAttributeID ?? raw.parentAttributeId,
    Order: raw.Order ?? raw.order ?? 0,
    AttirbuteLevel: raw.AttirbuteLevel ?? raw.AttributeLevel,
  }
}

/** Same as msa-frontend-gbp: build tree from flat attributes by Order and ParentAttributeID/AttributeID. */
export const buildTree = (attributes) => {
  if (!Array.isArray(attributes) || !attributes.length) return []
  attributes = attributes
    .map((a) => normalizeAttributeForTree(a))
    .filter((a) => a != null && a.AttributeID != null)
  let map = {}, roots = []
  attributes = attributes.sort((a, b) => a.Order - b.Order)
  for (let i = 0; i < attributes.length; i++) {
    map[attributes[i].AttributeID] = i
    attributes[i].Children = []
  }

  for (let i = 0; i < attributes.length; i++) {
    let node = attributes[i]
    const levelOne = node.AttirbuteLevel === 1 || detailsLevel(node) === 1
    if ((node.ParentAttributeID || node.ParentAttributeID === 0) && (node.ParentAttributeID !== node.AttributeID && findAttributeRecursive(attributes, node.ParentAttributeID) != null) && !levelOne) {
      attributes[map[node.ParentAttributeID]]?.Children.push(node)
    } else {
      roots.push(node)
    }
  }
  return roots
}

/**
 * Set IsExpanded on every node: true for nodes in the first `maxDepth` levels, false deeper.
 * Depth 1 = roots (same convention as VisualizeByName expandAttributes with ExpandLevel).
 */
export function setAttributeTreeExpandDepth(attributes, maxDepth, depth = 1) {
  if (!Array.isArray(attributes)) return
  for (const a of attributes) {
    a.IsExpanded = depth <= maxDepth
    if (a.Children && a.Children.length > 0) {
      setAttributeTreeExpandDepth(a.Children, maxDepth, depth + 1)
    }
  }
}

function findAttributeRecursive(attributes, attributeID) {
  for (let i = 0; i < attributes.length; i++) {
    if (attributes[i].AttributeID === attributeID) {
      return attributes[i];
    }

    if (attributes[i].Children && attributes[i].Children.length > 0) {
      let found = findAttributeRecursive(attributes[i].Children, attributeID);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

export const getChildAttributeIds = (attribute) => {
  let attributeIds = [attribute.AttributeID];
  if (attribute.Children && attribute.Children.length > 0) {
    for (let child of attribute.Children) {
      attributeIds = [...attributeIds, ...getChildAttributeIds(child)];
    }
  }
  return attributeIds;
}

export const getAllAttributeIds = (dimension) => {
  let attributeIds = [];
  dimension.Attributes.map(attribute => attributeIds = [...attributeIds, ...getChildAttributeIds(attribute)]);
  return attributeIds;
}

export const getAttributesMap = (attributes) => {
  const attributeMap = new Map();

  const flattenAttributes = (attributes) => {
    attributes.forEach(attribute => {
      attributeMap.set(attribute.AttributeID, attribute);
      if (attribute.Children && attribute.Children.length > 0) {
        flattenAttributes(attribute.Children);
      }
    });
  };

  flattenAttributes(attributes);

  return attributeMap;
}

function findAttributeByIdRecursive(attributes, attributeID) {
  if (!attributes || !Array.isArray(attributes)) return null
  for (let i = 0; i < attributes.length; i++) {
    if (attributes[i].AttributeID === attributeID) return attributes[i]
    const found = findAttributeByIdRecursive(attributes[i].Children, attributeID)
    if (found) return found
  }
  return null
}

/** Returns the attribute object (with DisplayName) for a given attribute ID within a dimension's tree. */
export const getAttributeById = (dimension, attributeID) =>
  findAttributeByIdRecursive(dimension?.Attributes ?? [], attributeID)

function isPlaceholderAttribute(attr) {
  if (!attr?.Details) return false
  try {
    const d = typeof attr.Details === 'string' ? JSON.parse(attr.Details) : attr.Details
    return d.placeholder === true
  } catch {
    return false
  }
}

/** Returns the first selectable (non-placeholder) attribute ID in tree order, or the first attribute ID if none. */
export function getFirstSelectableAttributeId(dimension) {
  const roots = dimension?.Attributes ?? []
  function walk(attrs) {
    for (const a of attrs) {
      if (!isPlaceholderAttribute(a)) return a.AttributeID
      const inChildren = walk(a.Children ?? [])
      if (inChildren != null) return inChildren
    }
    return null
  }
  const id = walk(roots)
  return id ?? roots[0]?.AttributeID ?? roots[0]?.attributeId
}